import React, { useCallback, useEffect, useRef, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Paper,
  styled,
  Tab,
  TableContainer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PerfectScrollbar from "react-perfect-scrollbar";
import moment from "moment";
import { getService } from "../../../reducers/service";
import { AMSModules, serviceNames } from "../../../utils/constants";
import SummaryTableToolbar from "./SummaryTableToolbar";
import { useDispatch, useSelector } from "react-redux";

const HEIGHT_OFFSETS = {
  scroll: 340,
  paper: 430,
  table: 320,
};
const MIN_HEIGHTS = {
  scroll: 320,
  paper: 400,
  table: 370,
};

const DataGridContainer = styled(TableContainer)(({ theme }) => ({
  overflowX: "auto",
  marginBottom: theme.spacing(2.5),
  marginTop: theme.spacing(2.5),
  minHeight: MIN_HEIGHTS.table,
}));

export const initialFilterState = {
  course: "",
  category: "",
  courseDateFrom: "",
  courseDateTo: "",
};

const Summary = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userAgency = useSelector((state) => state.user.userAgency);
  const filtersInfo = useSelector((state) => state.filtersInfo);
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState("1");
  const [dimensions, setDimensions] = useState({
    scrollHeight: Math.max(
      window.innerHeight - HEIGHT_OFFSETS.scroll,
      MIN_HEIGHTS.scroll
    ),
    paperHeight: Math.max(
      window.innerHeight - HEIGHT_OFFSETS.paper,
      MIN_HEIGHTS.paper
    ),
    tableHeight: Math.max(
      window.innerHeight - HEIGHT_OFFSETS.table,
      MIN_HEIGHTS.table
    ),
  });
  const [loading, setLoading] = useState(false);
  const [trainingCategoriesRows, setTrainingCategoriesRows] = useState([]);
  const [trainingCategoriesColumns, setTrainingCategoriesColumns] = useState(
    []
  );
  const [trainingSummaryRows, setTrainingSummaryRows] = useState([]);
  const [trainingSummaryColumns, setTrainingSummaryColumns] = useState([]);
  const [metricsRows, setMetricRows] = useState([]);
  const [metricsColumns, setMetricColumns] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [filters, setFilters] = useState(filtersInfo.summary || initialFilterState);
  const currentAgency = useRef(null);

  const fetchDropdownValues = async () => {
    const services = {
      distinct: getService(serviceNames.trainingCourseDistinct),
    };

    const [distinctResult] = await Promise.all([
      services.distinct.find({
        query: { column: ["CourseName", "Category"] },
      }),
    ]);

    const courseOptions =
      distinctResult?.CourseName.map((value) => ({
        label: value,
        value,
      })) || [];

    const categoryOptions =
      distinctResult?.Category.map((value) => ({
        label: value,
        value,
      })) || [];
    setCourseOptions(courseOptions);
    setCategoryOptions(categoryOptions);
  };

  const formatByCategory = (data = []) => {
    return data.map(item => ({
      ...item,
      Category: item.Category ?? "None"
    }));
  };


  const fetchData = async (filters = {}) => {
    setLoading(true);
    const service = getService(serviceNames.trainingSummary);

    const result1 = await service.find({
      query: {
        by: "category",
        courseDateStart: filters.courseDateFrom
          ? moment(filters.courseDateFrom).format("YYYY-MM-DD")
          : null,
        courseDateEnd: filters.courseDateTo
          ? moment(filters.courseDateTo).format("YYYY-MM-DD")
          : null,
        category: filters.category?.value || null,
        course: filters.course?.value || null,
      },
    });

    const dynamicTitles = result1?.titles.map((title) => ({
      field: title,
      headerName: title,
      width: 75,
      valueFormatter: (params) => `$${Number(params.value ?? 0).toFixed(2)}`,
    }));

    setTrainingCategoriesColumns([
      { field: "Category", headerName: "Category", width: 150 },
      { field: "TotalHours", headerName: "Hours", width: 75 },
      ...dynamicTitles,
    ]);

    const formattedData = formatByCategory(result1?.data);
    setTrainingCategoriesRows(
      formattedData.map((row, index) => ({ ...row, id: index + 1 }))
    );

    const result2 = await service.find({
      query: {
        by: "employee",
        courseDateStart: filters.courseDateFrom
          ? moment(filters.courseDateFrom).format("YYYY-MM-DD")
          : null,
        courseDateEnd: filters.courseDateTo
          ? moment(filters.courseDateTo).format("YYYY-MM-DD")
          : null,
        category: filters.category?.value || null,
        course: filters.course?.value || null,
      },
    });

    const dynamicTitles2 = result2?.titles.map((title) => ({
      field: title,
      headerName: title,
      width: 75,
      valueFormatter: (params) => `$${Number(params.value ?? 0).toFixed(2)}`,
    }));

    setTrainingSummaryColumns([
      { field: "EmployeeId", headerName: "Emp. #", width: 75 },
      { field: "EmployeeName", headerName: "Employee Name", width: 150 },
      { field: "Hours", headerName: "Hours", width: 75 },
      ...dynamicTitles2,
    ]);
    setTrainingSummaryRows(result2?.data);

    setMetricColumns([
      { field: "Metric", headerName: "Title", width: 100 },
      {
        field: "Hours",
        headerName: "Hours",
        width: 100,
        valueFormatter: (params) => {
          const value = Number(params.value);
          if (isNaN(value)) return "";

          return value.toFixed(2);
        },
      },
      ...result2?.titles.map((title) => ({
        field: title,
        headerName: title,
        width: 75,
        valueFormatter: (params) => `$${Number(params.value ?? 0).toFixed(2)}`,
      })),
    ]);
    setMetricRows(result2?.metrics);

    setLoading(false);
  };

  useEffect(() => {
    currentAgency.current = userAgency;
    fetchDropdownValues();
  }, []);

  useEffect(() => {
    const isAgencyChanged = currentAgency.current !== userAgency;
    if (isAgencyChanged) {
      resetFilters();
      currentAgency.current = userAgency;
    }
    fetchData(isAgencyChanged ? initialFilterState : filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleResize = useCallback(() => {
    setDimensions({
      scrollHeight: Math.max(
        window.innerHeight - HEIGHT_OFFSETS.scroll,
        MIN_HEIGHTS.scroll
      ),
      paperHeight: Math.max(
        window.innerHeight - HEIGHT_OFFSETS.paper,
        MIN_HEIGHTS.paper
      ),
      tableHeight: Math.max(
        window.innerHeight - HEIGHT_OFFSETS.table,
        MIN_HEIGHTS.table
      ),
    });
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const resetFilters = () => {
    setFilters(initialFilterState);
     dispatch({ type: AMSModules.Summary, payload: initialFilterState });
  };

  const handleOnFilterChange = (newFilters) => {
    setFilters(newFilters);
    dispatch({ type: AMSModules.Summary, payload: newFilters });
  }

  return (
    <Paper
      sx={{
        padding: theme.spacing(2),
        marginTop: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        overflow: "auto", // allow scrollbars
        height: isSmall ? "700px" : "calc(100vh - 85px)",
      }}
    >
      <SummaryTableToolbar
        onFilterChange={handleOnFilterChange}
        filters={filters}
        resetFilters={resetFilters}
        courseOptions={courseOptions}
        categoryOptions={categoryOptions}
      />
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="training summaries">
            <Tab label="Training Categories" value="1" />
            <Tab label="Training Summary" value="2" />
          </TabList>
        </Box>

        <TabPanel value="1">
          <DataGridContainer sx={{ maxHeight: dimensions.tableHeight }}>
            <PerfectScrollbar>
              <DataGridPro
                rows={trainingCategoriesRows}
                columns={trainingCategoriesColumns}
                loading={loading}
                disableSelectionOnClick
                sx={{
                  color: theme.palette.colorFont,
                  height: "calc(100vh - 320px)",
                }}
              />
            </PerfectScrollbar>
          </DataGridContainer>
        </TabPanel>

        <TabPanel value="2">
          <Box sx={{ overflowX: "auto" }}>
            <Box display="flex" gap={2} minWidth="650px" flexWrap="wrap">
              <Box flex="1 1 300px">
                <DataGridContainer sx={{ maxHeight: dimensions.tableHeight }}>
                  <PerfectScrollbar>
                    <DataGridPro
                      rows={trainingSummaryRows}
                      columns={trainingSummaryColumns}
                      getRowId={(row) => row.EmployeeId}
                      loading={loading}
                      disableSelectionOnClick
                      sx={{
                        color: theme.palette.colorFont,
                        height: "calc(100vh - 320px)",
                      }}
                    />
                  </PerfectScrollbar>
                </DataGridContainer>
              </Box>

              <Box flex="1 1 300px">
                <DataGridContainer sx={{ maxHeight: dimensions.tableHeight }}>
                  <PerfectScrollbar>
                    <DataGridPro
                      rows={metricsRows}
                      columns={metricsColumns}
                      getRowId={(row) => row.Metric}
                      loading={loading}
                      disableSelectionOnClick
                      hideFooter
                      sx={{
                        color: theme.palette.colorFont,
                        height: "calc(100vh - 320px)",
                      }}
                    />
                  </PerfectScrollbar>
                </DataGridContainer>
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </TabContext>
    </Paper>
  );
};

export default Summary;
